import * as React from 'react';

import { assert, assertNever } from '@gitbook/util-ts';

import { Separator } from '../Divider';
import { Heading } from '../Heading';
import { Popover } from '../Popover';
import { AutoComplete } from './AutoComplete';
import { Menu } from './Menu';
import { MenuItem } from './MenuItem';
import { MenuPlaceholder } from './MenuPlaceholder';
import { MenuSection } from './MenuSection';
import { SubMenu } from './SubMenu';

const TOP_LEVEL_COMPONENTS = [
    MenuItem,
    MenuSection,
    SubMenu,
    Separator,
    MenuPlaceholder,
] satisfies ComponentList;

export type AnyMenuNode = MenuItemNode | SeparatorNode | HeadingNode | PlaceholderNode;

type MenuItemNode = {
    id: string;
    type: 'item';
    disabled: boolean;
    title: string | null;
    subtitle: string | null;
    keywords: string[];
    parent: MenuItemNode | null;
    children: AnyMenuNode[] | null;
    itemElement: ReactElementOfType<typeof MenuItem>;
    element: ReactElementFromComponents<[typeof MenuItem, typeof SubMenu]>;
};

type PlaceholderNode = {
    id: string;
    type: 'placeholder';
    element: ReactElementOfType<typeof MenuPlaceholder>;
};

type SeparatorNode = {
    id: string;
    type: 'separator';
    element: ReactElementOfType<typeof Separator>;
};

type HeadingNode = {
    id: string;
    type: 'heading';
    element: ReactElementOfType<typeof Heading>;
};

type ComponentList = readonly React.ComponentType<any>[];

type ReactElementFromComponents<T extends ComponentList> = T[number] extends React.ComponentType<
    infer P
>
    ? React.ReactElement<P, T[number]>
    : never;

/**
 * Check if the node is valid in a Menu.
 */
function checkIsValidReactNode<T extends ComponentList>(
    child: React.ReactNode,
    allowedComponents: T
): child is ReactElementFromComponents<T> {
    return (
        React.isValidElement(child) &&
        typeof child.type !== 'string' &&
        allowedComponents.includes(child.type)
    );
}

function getNodeDisplayName(node: React.ReactNode): string {
    if (node == null || typeof node === 'boolean') {
        return 'Nullish';
    }

    if (typeof node === 'string' || typeof node === 'number') {
        return 'Text';
    }

    if (Array.isArray(node)) {
        return 'Array';
    }

    if (React.isValidElement(node)) {
        return getElementDisplayName(node);
    }

    return 'UnknownNode';
}

function getElementDisplayName(element: React.ReactElement): string {
    const type = element.type;

    if (typeof type === 'string') {
        return type;
    }

    if (type && 'displayName' in type && typeof type.displayName === 'string') {
        return type.displayName;
    }

    if (type && typeof type.name === 'string' && type.name.length > 0) {
        return type.name;
    }

    if (type && (type as any).type) {
        return getElementDisplayName({ type: (type as any).type } as any);
    }

    return 'AnonymousComponent';
}

/**
 * Assert that the node is valid in a Menu.
 */
function assertIsValidReactNode<T extends ComponentList>(
    child: React.ReactNode,
    allowedComponents: T
): asserts child is ReactElementFromComponents<T> {
    if (!checkIsValidReactNode(child, allowedComponents)) {
        throw new Error(`${getNodeDisplayName(child)} is not allowed in a Menu`);
    }
}

type ReactElementOfType<T extends React.JSXElementConstructor<any>> = React.ReactElement<
    React.ComponentProps<T>,
    T
>;

/**
 * Estimate the size of a menu node.
 */
export function estimateMenuNodeSize(node: AnyMenuNode): {
    width: number;
    height: number;
} {
    switch (node.type) {
        case 'heading':
            return { height: 24, width: 0 };
        case 'separator':
            return { height: 9, width: 0 };
        case 'placeholder':
            return { height: 52, width: 0 };
        case 'item': {
            let width = 14 + 12;
            let height = 32;
            if (node.itemElement.props.icon) {
                width += 24;
            }
            if (node.children) {
                width += 24;
            }
            if (node.itemElement.props.checked !== undefined) {
                width += 24;
            }
            const titleString = getStringFromReactNode(node.itemElement.props.children);
            const subtitleString = getStringFromReactNode(node.itemElement.props.subtitle);
            width += Math.max(
                titleString ? titleString.length * 7 : 0,
                subtitleString ? subtitleString.length * 6 : 0
            );
            if (subtitleString) {
                height += 16;
            }
            return { height, width };
        }
        default:
            assertNever(node);
    }
}

/**
 * Get a menu tree from menu children.
 */
export function getMenuTreeFromChildren(children: React.ReactNode, id: string): AnyMenuNode[] {
    return getMenuNodesFromChildren(children, id, TOP_LEVEL_COMPONENTS);
}

/**
 * Get menu nodes from React children.
 */
function getMenuNodesFromChildren(
    children: React.ReactNode,
    prefix: string,
    allowedComponents: ComponentList
): AnyMenuNode[] {
    return React.Children.toArray(children).flatMap((child) => {
        assertIsValidReactNode(child, allowedComponents);

        if (!child.key) {
            throw new Error(`All elements should have key`);
        }

        const id = `${prefix}/${child.key}`;

        if (checkIsValidReactNode(child, [MenuSection])) {
            return getMenuNodesFromChildren(child.props.children, id, [
                Heading,
                ...TOP_LEVEL_COMPONENTS,
            ]);
        }

        if (checkIsValidReactNode(child, [MenuPlaceholder])) {
            return [
                {
                    id,
                    type: 'placeholder' as const,
                    element: child,
                },
            ];
        }

        if (checkIsValidReactNode(child, [Heading])) {
            return [
                {
                    id,
                    type: 'heading' as const,
                    element: child,
                },
            ];
        }

        if (checkIsValidReactNode(child, [Separator])) {
            return [
                {
                    id,
                    type: 'separator' as const,
                    element: child,
                },
            ];
        }

        if (checkIsValidReactNode(child, [MenuItem, SubMenu])) {
            const itemElement = getMenuItemElement(child);
            assert(itemElement, 'A menu item should exist');

            const itemNode: MenuItemNode = {
                id,
                type: 'item',
                disabled: itemElement.props.disabled ?? false,
                title:
                    itemElement.props.textValue ??
                    getStringFromReactNode(itemElement.props.children),
                subtitle:
                    itemElement.props.subtitleTextValue ??
                    getStringFromReactNode(itemElement.props.subtitle),
                keywords: itemElement.props.keywords ?? [],
                element: child,
                parent: null,
                children: null,
                itemElement,
            };

            if (checkIsValidReactNode(child, [SubMenu])) {
                itemNode.children = ((): AnyMenuNode[] => {
                    const popover = React.Children.toArray(child.props.children).find((child) =>
                        checkIsValidReactNode(child, [Popover])
                    );

                    if (!popover) {
                        return [];
                    }

                    const menu = (() => {
                        const menuOrAutoComplete = React.Children.toArray(
                            popover.props.children
                        ).find((child) => checkIsValidReactNode(child, [Menu, AutoComplete]));

                        if (!menuOrAutoComplete) {
                            return null;
                        }

                        if (menuOrAutoComplete.type === AutoComplete) {
                            return React.Children.toArray(menuOrAutoComplete.props.children).find(
                                (child) => checkIsValidReactNode(child, [Menu])
                            );
                        }

                        return menuOrAutoComplete;
                    })();

                    if (menu) {
                        return getMenuNodesFromChildren(
                            menu.props.children,
                            id,
                            TOP_LEVEL_COMPONENTS
                        ).map((node): AnyMenuNode => {
                            if (node.type === 'item') {
                                return { ...node, parent: itemNode };
                            }
                            return node;
                        });
                    }

                    return [];
                })();

                return [itemNode];
            }

            return [itemNode];
        }

        throw new Error(`Unknown element ${child}`);
    });
}

/**
 * Get a React node as string if possible.
 */
function getStringFromReactNode(node: React.ReactNode) {
    return node && typeof node === 'string' ? node : null;
}

/**
 * Get a menu item element from a child.
 */
function getMenuItemElement(child: React.ReactNode) {
    if (checkIsValidReactNode(child, [MenuItem])) {
        return child;
    }
    if (checkIsValidReactNode(child, [SubMenu])) {
        const menuItemElement = React.Children.toArray(child.props.children).find((child) =>
            checkIsValidReactNode(child, [MenuItem])
        );
        if (!menuItemElement) {
            return null;
        }
        return menuItemElement;
    }
    return null;
}
